import styled from 'styled-components';
import { motion } from 'framer-motion';

// Teal color constant for Enroll Now button (matches existing #1F6F78)
const TEAL_SECONDARY = '#1F6F78';

export const LoginContainer = styled.div`
  width: 100%;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  position: relative;
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: color 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

export const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

export const EnrollNowButton = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: ${TEAL_SECONDARY};
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

// Soft Alert Banner for errors/session messages
export const AlertBanner = styled(motion.div)`
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: var(--card-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const AlertIcon = styled.div`
  color: rgba(220, 53, 69, 0.9);
  font-size: 1.25rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

export const AlertText = styled.div`
  color: rgba(220, 53, 69, 0.95);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;
`;

export const GreetingWrapper = styled.h2`
  text-align: center;
  width: 100%;
  margin-bottom: 1.25rem;
  margin-top: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  font-family: Arial, "Segoe UI", "Noto Sans Arabic", "Arabic UI Text", sans-serif;

  .arabic-text {
    display: inline-block;
    direction: rtl;
    unicode-bidi: bidi-override;
    text-align: center;
    width: 100%;
  }
`;

export const ForgotUsernameLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);

  button {
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: inherit;
    transition: color 0.2s ease, text-decoration 0.2s ease;
    padding: 0.25rem 0.5rem;
    white-space: nowrap;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--primary-color);
        text-decoration: underline;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  .separator {
    color: var(--text-muted);
    opacity: 0.5;
    user-select: none;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    gap: 0.375rem;

    button {
      padding: 0.25rem 0.375rem;
      font-size: 0.8125rem;
    }
  }
`;

export const HelpLink = styled.div`
  text-align: center;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: var(--text-muted);

  button {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: inherit;
    transition: color 0.2s ease, text-decoration 0.2s ease;
    padding: 0.25rem 0.5rem;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--text-secondary);
        text-decoration: underline;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid var(--text-inverse);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const MethodToggle = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.25rem;
  background-color: var(--background-secondary, var(--background-primary));
  border: 1px solid var(--border-color);
  border-radius: var(--control-radius, var(--card-radius));
  margin-bottom: 1.5rem;
`;

export const MethodToggleButton = styled.button`
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: calc(var(--control-radius, var(--card-radius)) - 4px);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.$active ? 'var(--text-inverse)' : 'var(--text-secondary)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  @media (max-width: 360px) {
    font-size: 0.72rem;
    padding: 0.5rem 0.3rem;
    letter-spacing: -0.01em;
  }
`;

export const OtpHeading = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

export const OtpDescription = styled.p`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 0 0 0.25rem;
  line-height: 1.5;

  strong {
    color: var(--text-primary);
  }
`;

export const OtpInfoText = styled.p`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: -0.5rem 0 0.25rem;
`;

export const OtpBoxRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;

  @media (max-width: 360px) {
    gap: 0.35rem;
  }
`;

export const OtpBox = styled.input`
  flex: 1 1 0;
  min-width: 0;
  max-width: 48px;
  aspect-ratio: 1;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  border: 1px solid var(--border-color);
  border-radius: var(--control-radius, var(--card-radius));
  background-color: var(--background-primary);
  color: var(--text-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  @media (max-width: 360px) {
    font-size: 1.15rem;
  }
`;

export const OtpResendText = styled.p`
  text-align: center;
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: -0.5rem 0 0;

  button {
    background: none;
    border: none;
    color: var(--primary-color);
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        text-decoration: underline;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

export const OtpSwitchEmail = styled.div`
  text-align: center;

  button {
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0.25rem 0;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--primary-color);
        text-decoration: underline;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;
